// @ts-nocheck
import { useEffect, useState } from 'react'
import { Tag, X, AlertCircle, CheckCircle2, Plus, Trash2, GripVertical } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

export default function PhoneNumberOptionsSettings({ phoneNumber, onClose }) {
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  // 通話種別オプション
  const [callTypeOptions, setCallTypeOptions] = useState([])
  const [newCallTypeLabel, setNewCallTypeLabel] = useState('')

  // よく使うタグ
  const [quickTags, setQuickTags] = useState([])
  const [newQuickTagLabel, setNewQuickTagLabel] = useState('')

  // デフォルト値
  const defaultCallTypeOptions = [
    { value: 'inquiry', label: '問い合わせ' },
    { value: 'complaint', label: 'クレーム' },
    { value: 'order', label: '注文' },
    { value: 'support', label: 'サポート' },
    { value: 'other', label: 'その他' },
  ]

  const defaultQuickTags = [
    { value: 'callback', label: '折り返し希望', isDefault: false },
    { value: 'urgent', label: '緊急', isDefault: false },
    { value: 'vip', label: 'VIP', isDefault: false },
    { value: 'first-contact', label: '初回問い合わせ', isDefault: false },
  ]

  // 電話番号の設定を取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        // 電話番号リストから該当の電話番号を取得（callTypeOptions, quickTagsを含む）
        const phoneData = await api.get('/api/phone-numbers')
        const currentPhone = phoneData.items?.find(p => p.phoneNumberId === phoneNumber.phoneNumberId)

        if (currentPhone) {
          setCallTypeOptions(currentPhone.callTypeOptions || defaultCallTypeOptions)
          setQuickTags(currentPhone.quickTags || defaultQuickTags)
        } else {
          setCallTypeOptions(defaultCallTypeOptions)
          setQuickTags(defaultQuickTags)
        }
      } catch (err) {
        console.error('データ取得エラー:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (phoneNumber?.phoneNumberId) {
      fetchData()
    }
  }, [phoneNumber?.phoneNumberId, api])

  // 設定を保存
  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      await api.put(`/api/phone-numbers/${phoneNumber.phoneNumberId}`, {
        callTypeOptions,
        quickTags,
      })

      setSaved(true)
      setTimeout(() => {
        setSaved(false)
        onClose()
      }, 1500)
    } catch (err) {
      console.error('保存エラー:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // 通話種別を追加
  const handleAddCallType = () => {
    if (!newCallTypeLabel.trim()) {
      return
    }
    const newValue = crypto.randomUUID()
    setCallTypeOptions([...callTypeOptions, { value: newValue, label: newCallTypeLabel.trim() }])
    setNewCallTypeLabel('')
  }

  // 通話種別を削除
  const handleRemoveCallType = (valueToRemove) => {
    setCallTypeOptions(callTypeOptions.filter(opt => opt.value !== valueToRemove))
  }

  // 通話種別を移動
  const handleMoveCallType = (index, direction) => {
    const newOptions = [...callTypeOptions]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newOptions.length) return
    ;[newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]]
    setCallTypeOptions(newOptions)
  }

  // よく使うタグを追加
  const handleAddQuickTag = () => {
    if (!newQuickTagLabel.trim()) {
      return
    }
    const newValue = crypto.randomUUID()
    setQuickTags([...quickTags, { value: newValue, label: newQuickTagLabel.trim(), isDefault: false }])
    setNewQuickTagLabel('')
  }

  // デフォルトタグの切り替え
  const handleToggleDefaultTag = (value) => {
    setQuickTags(quickTags.map(tag =>
      tag.value === value ? { ...tag, isDefault: !tag.isDefault } : tag
    ))
  }

  // よく使うタグを削除
  const handleRemoveQuickTag = (valueToRemove) => {
    setQuickTags(quickTags.filter(opt => opt.value !== valueToRemove))
  }

  // よく使うタグを移動
  const handleMoveQuickTag = (index, direction) => {
    const newTags = [...quickTags]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newTags.length) return
    ;[newTags[index], newTags[targetIndex]] = [newTags[targetIndex], newTags[index]]
    setQuickTags(newTags)
  }

  // デフォルトにリセット
  const handleResetToDefault = () => {
    if (confirm('通話種別とタグをデフォルトにリセットしますか？')) {
      setCallTypeOptions(defaultCallTypeOptions)
      setQuickTags(defaultQuickTags)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-500 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
            <Tag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              タグ・通話種別設定
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {phoneNumber.name} ({formatPhoneNumberForDisplay(phoneNumber.phoneNumber)})
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* エラーメッセージ */}
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm font-medium text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* 保存成功メッセージ */}
      {saved && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            設定を保存しました
          </p>
        </div>
      )}

      {/* 説明 */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
          この電話番号への着信に対する通話履歴で使用する<strong>通話種別</strong>と<strong>よく使うタグ</strong>を設定できます。
        </p>
        <p className="text-xs text-blue-700 dark:text-blue-400">
          💡 タグの<CheckCircle2 className="w-3 h-3 inline mx-0.5" />ボタンをクリックすると、通話メモ作成時にデフォルトで選択されます。
        </p>
      </div>

      <div className="space-y-6 max-h-[50vh] overflow-y-auto pr-2">
        {/* 通話種別オプション */}
        <div>
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Tag className="w-4 h-4" />
            通話種別
          </h3>
          <div className="space-y-2 mb-3">
            {callTypeOptions.map((option, index) => (
              <div
                key={option.value}
                className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => handleMoveCallType(index, 'up')}
                    disabled={index === 0}
                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <GripVertical className="w-4 h-4 rotate-90" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveCallType(index, 'down')}
                    disabled={index === callTypeOptions.length - 1}
                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <GripVertical className="w-4 h-4 -rotate-90" />
                  </button>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900 dark:text-white">{option.label}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveCallType(option.value)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {callTypeOptions.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                通話種別が設定されていません
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newCallTypeLabel}
              onChange={(e) => setNewCallTypeLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCallType()}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              placeholder="新しい通話種別"
            />
            <button
              type="button"
              onClick={handleAddCallType}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* よく使うタグ */}
        <div>
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Tag className="w-4 h-4" />
            よく使うタグ
          </h3>
          <div className="space-y-2 mb-3">
            {quickTags.map((tag, index) => (
              <div
                key={tag.value}
                className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => handleMoveQuickTag(index, 'up')}
                    disabled={index === 0}
                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <GripVertical className="w-4 h-4 rotate-90" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveQuickTag(index, 'down')}
                    disabled={index === quickTags.length - 1}
                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <GripVertical className="w-4 h-4 -rotate-90" />
                  </button>
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <p className="text-sm text-gray-900 dark:text-white">{tag.label}</p>
                  {tag.isDefault && (
                    <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                      デフォルト
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleDefaultTag(tag.value)}
                  className={`p-2 rounded-lg transition-colors ${
                    tag.isDefault
                      ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                      : 'text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  }`}
                  title={tag.isDefault ? 'デフォルト選択を解除' : 'デフォルトで選択'}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveQuickTag(tag.value)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {quickTags.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                よく使うタグが設定されていません
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newQuickTagLabel}
              onChange={(e) => setNewQuickTagLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddQuickTag()}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              placeholder="新しいタグ"
            />
            <button
              type="button"
              onClick={handleAddQuickTag}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* フッター */}
      <div className="flex justify-between gap-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={handleResetToDefault}
          className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
        >
          デフォルトにリセット
        </button>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 font-medium transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}
